import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { postponePatientOutreach } from "actions/patient-outreaches";
import ActionModal from "./action-modal";

const pad = n => String(n).padStart(2, "0");
const toIsoDate = d =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const PostponeModal = ({ row, onClose }) => {
  const dispatch = useDispatch();
  const { todayIso, tomorrowIso, maxIso } = useMemo(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const max = new Date(today);
    max.setDate(today.getDate() + 365);
    return {
      todayIso: toIsoDate(today),
      tomorrowIso: toIsoDate(tomorrow),
      maxIso: toIsoDate(max)
    };
  }, []);

  const [newDate, setNewDate] = useState(tomorrowIso);

  // Close the modal up front. Postpone is the only single-row action that
  // REMOVES the row from state on success — that unmounts RowActionMenu
  // mid-await, which races with onClose() and can leave a ghost modal on
  // screen. Closing first avoids the race; success / blocked / error
  // feedback all come through toasts dispatched by the action creator.
  const handleConfirm = () => {
    dispatch(postponePatientOutreach(row.patientGuid, newDate));
    onClose();
  };

  return (
    <ActionModal title="Postpone outreach" onClose={onClose}>
      <p>
        Postpone outreach for{" "}
        <strong>{row.patientName || row.patientGuid}</strong>.
      </p>
      <label className="patient-outreach-action-modal__field">
        New outreach date:{" "}
        <input
          type="date"
          value={newDate}
          min={todayIso}
          max={maxIso}
          onChange={e => setNewDate(e.target.value)}
        />
      </label>
      <p className="patient-outreach-action-modal__hint">
        The row will be removed from today&apos;s pool. The patient will become
        re-eligible on the new outreach date (subject to the eligibility
        rules).
      </p>

      <div className="patient-outreach-action-modal__footer">
        <button type="button" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="patient-outreach-action-modal__primary"
          onClick={handleConfirm}
          disabled={!newDate}
        >
          Postpone
        </button>
      </div>
    </ActionModal>
  );
};

PostponeModal.propTypes = {
  row: PropTypes.shape({
    patientGuid: PropTypes.string.isRequired,
    patientName: PropTypes.string
  }).isRequired,
  onClose: PropTypes.func.isRequired
};

export default PostponeModal;
